
import { debounce } from 'lodash';
import { defineComponent, nextTick, ref } from 'vue';
import { IDanhMucToaXeResponseBase } from '@/core/interfaces/ApiResponses';
import { usePagination } from '@/core/hooks/use-pagination';
import { swalNotification } from '@/core/helpers/utils';
import { useRouterCustom } from '@/core/hooks/use-router-custom';
import { usePageHeader } from '@/core/hooks/usePageHeader';
import { DANH_MUC_TOA_XE } from '@/core/constants/permissions';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

import DanhMucToaXeService from '@/core/services/DanhMucToaXe.service';
import DanhMucToaXeTable from '@/views/crafted/pages/danh-muc-toa-xe/danh-muc-toa-xe-table/DanhMucToaXeTable.vue';
import Pagination from '@/components/shared/pagination/Pagination.vue';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageHeaderActions from '@/layout/header/PageHeaderActions.vue';
import PageHeaderActionItem from '@/layout/header/PageHeaderActionItem.vue';

export default defineComponent({
  name: 'danh-muc-toa-xe',

  components: {
    DanhMucToaXeTable,
    Pagination,
    PageContentBase,
    PageHeaderActions,
    PageHeaderActionItem,
  },

  setup() {
    const danhMucToaXeItems = ref<IDanhMucToaXeResponseBase[]>([]);
    const { push } = useRouterCustom();
    const {
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
    } = usePagination();

    document.title = 'Nhóm toa xe - Đường sắt Hà Nội';

    const { searchQuery } = usePageHeader({
      title: 'Danh sách nhóm toa xe',
      breadcrumbs: [{ text: 'Nhóm toa xe' }],
      showSearch: true,
      searchPlaceholder: 'Tìm theo mã hoặc tên',
      addLabel: 'Thêm nhóm toa xe',
      addPermissions: [DANH_MUC_TOA_XE.TAO_MOI],
      onAdd: () => push('/quan-ly/danh-muc-toa-xe/tao-moi'),
    });

    return {
      danhMucToaXeItems,
      currentPage,
      totalItems,
      perPage,
      perPageArr,
      loading,
      searchQuery,
      faPrint,
    };
  },

  async mounted() {
    await this.fetchData();
  },

  watch: {
    async currentPage() {
      await this.fetchData();
    },

    async perPage() {
      if (this.currentPage === 1) {
        await this.fetchData();
        return;
      }
      this.currentPage = 1;
    },

    searchQuery: debounce(function () {
      nextTick().then(async () => {
        if (this.currentPage === 1) {
          await this.fetchData();
          return;
        }
        this.currentPage = 1;
      });
    }, 500),
  },

  methods: {
    openPrintViewer() {
      const route = this.$router.resolve({
        name: 'xem-danh-sach-danh-muc-toa-xe',
        query: this.searchQuery ? { s: this.searchQuery } : {},
      });

      window.open(route.href, '_blank');
    },

    async fetchData() {
      try {
        this.loading = true;

        const {
          data: {
            data: { data, total },
          },
        } = await DanhMucToaXeService.list(
          this.currentPage,
          this.perPage,
          this.searchQuery
        );

        this.danhMucToaXeItems = data;
        this.totalItems = total;
        this.loading = false;
      } catch {
        await swalNotification(
          'Có lỗi xảy ra khi lấy danh sách nhóm toa xe',
          'error'
        );
        this.loading = false;
      }
    },
  },
});
